<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { BaseButton } from '@/components/base'

interface Props {
  isOpen: boolean
  title: string
  description: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'success' | 'info'
  isLoading?: boolean
  error?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'danger',
  isLoading: false,
})

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()

const { t } = useI18n()

const iconBgClass: Record<string, string> = {
  danger:  'bg-red-100',
  warning: 'bg-yellow-100',
  success: 'bg-green-100',
  info:    'bg-primary-100',
}

const iconColorClass: Record<string, string> = {
  danger:  'text-red-600',
  warning: 'text-yellow-600',
  success: 'text-green-600',
  info:    'text-primary-600',
}

const confirmBtnClass: Record<string, string> = {
  danger:  'bg-red-600 hover:bg-red-700',
  warning: 'bg-yellow-600 hover:bg-yellow-700',
  success: 'bg-green-600 hover:bg-green-700',
  info:    'bg-primary-600 hover:bg-primary-700',
}
</script>

<template>
  <!-- Overlay -->
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 transition-opacity duration-200"
  >
    <!-- Modal -->
    <div class="bg-white rounded-lg shadow-xl max-w-sm w-full animate-in">
      <!-- Icon -->
      <div class="flex justify-center pt-6">
        <div :class="['w-12 h-12 rounded-full flex items-center justify-center', iconBgClass[props.variant]]">
          <!-- Danger: trash/warning -->
          <svg v-if="props.variant === 'danger'" :class="['w-6 h-6', iconColorClass[props.variant]]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <!-- Warning: exclamation -->
          <svg v-else-if="props.variant === 'warning'" :class="['w-6 h-6', iconColorClass[props.variant]]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <!-- Success: check -->
          <svg v-else-if="props.variant === 'success'" :class="['w-6 h-6', iconColorClass[props.variant]]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <!-- Info: info circle -->
          <svg v-else :class="['w-6 h-6', iconColorClass[props.variant]]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      <!-- Content -->
      <div class="px-6 py-6">
        <div class="text-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ props.title }}</h3>
          <p class="text-gray-600 text-sm whitespace-pre-line">{{ props.description }}</p>
        </div>

        <!-- Error Message -->
        <div v-if="props.error" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-700 text-sm font-medium">{{ props.error }}</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg flex gap-3 justify-end">
        <BaseButton
          @click="emit('cancel')"
          :disabled="isLoading"
          variant="secondary"
        >
          {{ cancelText || t('common.cancel') }}
        </BaseButton>
        <button
          @click="emit('confirm')"
          :disabled="isLoading"
          :class="['px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2', confirmBtnClass[props.variant]]"
        >
          <svg v-if="isLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span>{{ confirmText || t('common.yes') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes slideIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-in {
  animation: slideIn 0.2s ease-out;
}
</style>
